// src/App.js
import React, { Suspense, useMemo } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

// Code-splitting
const Login            = React.lazy(() => import("./components/Login"));
const Home             = React.lazy(() => import("./components/Home"));
const HomeContent      = React.lazy(() => import("./components/HomeContent"));
const WhatsappContent  = React.lazy(() => import("./components/WhatsappContent"));
const SettingsContent  = React.lazy(() => import("./components/SettingsContent"));
const Contacts         = React.lazy(() => import("./components/Contacts"));
const BulkMessage      = React.lazy(() => import("./components/BulkMessage"));
const NewUser          = React.lazy(() => import("./components/NewUser"));
const StatsPage        = React.lazy(() => import("./components/StatsPage"));
const QuickReplies     = React.lazy(() => import("./components/QuickReplies"));
const Tags             = React.lazy(() => import("./components/Tags"));
const Meta             = React.lazy(() => import("./components/meta"));
const Templates        = React.lazy(() => import("./components/Templates"));
const FirmList         = React.lazy(() => import("./components/FirmList"));
const FirmCreate       = React.lazy(() => import("./components/FirmCreate"));
const FirmDetail       = React.lazy(() => import("./components/FirmDetail"));
const Orders = React.lazy(() => import("./components/orders"));



// ✅ NEW: cart-template page
const CartTemplateSettings = React.lazy(() =>
  import("./components/CartTemplateSettings")
);

const Loader = () => <div style={{ padding: 24 }}>Yükleniyor…</div>;

/* ---------------- Auth helpers ---------------- */
const useAuth = () => {
  let isletmeId = window.localStorage.getItem("userId");
  let admin = 0;

  let userObj = {};
  try {
    userObj = JSON.parse(window.localStorage.getItem("user") || "{}");
  } catch {
    userObj = {};
  }

  if (!isletmeId && userObj?.isletme_id) isletmeId = String(userObj.isletme_id);
  admin = Number(userObj?.admin) || 0;

  const token = window.localStorage.getItem("token") || null;

  return useMemo(
    () => ({
      isAuthenticated: !!isletmeId,
      isletmeId,
      token,
      admin,
      user: userObj,
    }),
    [isletmeId, token, admin]
  );
};

const RequireAuth = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return children;
};

// ✅ Admin koruması (admin=1 değilse home'a at)
const RequireAdmin = ({ children }) => {
  const { isAuthenticated, admin } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!Number(admin)) {
    return <Navigate to="/home" replace state={{ from: location }} />;
  }
  return children;
};

const RedirectIfAuthed = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (isAuthenticated) {
    return <Navigate to="/" replace state={{ from: location }} />;
  }
  return children;
};

const Logout = () => {
  try {
    window.localStorage.removeItem("userId");
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("user");
    window.localStorage.removeItem("businessName");
  } catch {}
  return <Navigate to="/login" replace />;
};

/* ---------------- App ---------------- */
export default function App() {
  return (
    <Router>
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* Public */}
          <Route
            path="/login"
            element={
              <RedirectIfAuthed>
                <Login />
              </RedirectIfAuthed>
            }
          />
          <Route path="/logout" element={<Logout />} />

          {/* Protected layout */}
          <Route
            path="/"
            element={
              <RequireAuth>
                <Home />
              </RequireAuth>
            }
          >
            {/* herkes (admin 0/1) */}
            <Route index element={<HomeContent />} />
            <Route path="home" element={<HomeContent />} />

            {/* ✅ META (admin-only) */}
            <Route
              path="meta"
              element={
                <RequireAdmin>
                  {React.createElement(Meta)}
                </RequireAdmin>
              }
            />

            {/* ✅ TEMPLATES (admin-only) */}
            <Route
              path="templates"
              element={
                <RequireAdmin>
                  <Templates />
                </RequireAdmin>
              }
            />

            {/* ✅ CART TEMPLATE SETTINGS (admin-only) */}
            <Route
              path="cart-template"
              element={
                <RequireAdmin>
                  <CartTemplateSettings />
                </RequireAdmin>
              }
            />

            {/* admin-only sayfalar */}
            <Route
              path="whatsapp"
              element={
                <RequireAdmin>
                  <WhatsappContent />
                </RequireAdmin>
              }
            />

            <Route
              path="settings"
              element={
                <RequireAdmin>
                  <SettingsContent />
                </RequireAdmin>
              }
            />

            <Route
              path="bulk"
              element={
                <RequireAdmin>
                  <BulkMessage />
                </RequireAdmin>
              }
            />

            <Route
              path="contacts"
              element={
                <RequireAdmin>
                  <Contacts />
                </RequireAdmin>
              }
            />

            <Route
              path="users"
              element={
                <RequireAdmin>
                  <NewUser />
                </RequireAdmin>
              }
            />

            <Route
              path="StatsPage"
              element={
                <RequireAdmin>
                  <StatsPage />
                </RequireAdmin>
              }
            />

            <Route
              path="QuickReplies"
              element={
                <RequireAdmin>
                  <QuickReplies />
                </RequireAdmin>
              }
            />

            <Route
              path="tags"
              element={
                <RequireAdmin>
                  <Tags />
                </RequireAdmin>
              }
            />


            
            <Route
              path="orders"
              element={
                <RequireAdmin>
                  <Orders />
                </RequireAdmin>
              }
            />


            {/* ✅ Firms */}
            <Route
              path="firms"
              element={
                <RequireAdmin>
                  <FirmList />
                </RequireAdmin>
              }
            />

            <Route
              path="firms/new"
              element={
                <RequireAdmin>
                  <FirmCreate />
                </RequireAdmin>
              }
            />

            <Route
              path="firms/:id"
              element={
                <RequireAdmin>
                  <FirmDetail />
                </RequireAdmin>
              }
            />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Router>
  );
}
